import { haversineKm } from "./cost.js";

const routeCache = new Map();
const matrixCache = new Map();

function coordinateKey(point) {
  return `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`;
}

async function postJson(url, body, signal) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 14000);
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  try {
    const response = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export async function loadRoadRoute(origin, destination, signal, via = null) {
  const key = `${coordinateKey(origin)}>${via ? `${coordinateKey(via)}>` : ""}${coordinateKey(destination)}`;
  if (!routeCache.has(key)) {
    routeCache.set(key, postJson("/api/road-route", { origin, destination, via }).catch((error) => {
      routeCache.delete(key);
      throw error;
    }));
  }
  const shared = routeCache.get(key);
  if (!signal) return shared;
  if (signal.aborted) throw new DOMException("Petición cancelada", "AbortError");
  return new Promise((resolve, reject) => {
    const abort = () => { signal.removeEventListener("abort", abort); reject(new DOMException("Petición cancelada", "AbortError")); };
    signal.addEventListener("abort", abort, { once: true });
    shared.then(
      (value) => { signal.removeEventListener("abort", abort); resolve(value); },
      (error) => { signal.removeEventListener("abort", abort); reject(error); },
    );
  });
}

export async function loadRoadMetrics(origin, destination, candidates, signal) {
  const ids = candidates.map((station) => station.id).join(",");
  const key = `${coordinateKey(origin)}>${destination ? coordinateKey(destination) : "-"}:${ids}`;
  if (!matrixCache.has(key)) {
    matrixCache.set(
      key,
      postJson(
        "/api/road-matrix",
        {
          origin,
          destination,
          candidates: candidates.map(({ id, lat, lng }) => ({ id, lat, lng })),
        },
        signal,
      ).catch((error) => {
        matrixCache.delete(key);
        throw error;
      }),
    );
  }
  return matrixCache.get(key);
}

function uniqueCandidates(groups, limit = 25) {
  const seen = new Set();
  const result = [];
  for (const group of groups) {
    for (const station of group) {
      if (seen.has(station.id)) continue;
      seen.add(station.id);
      result.push(station);
      if (result.length >= limit) return result;
    }
  }
  return result;
}

export function selectNearbyCandidates(stations, fuel, origin, radiusKm) {
  if (!origin) return [];
  const eligible = stations
    .map((station) => ({ ...station, candidateDistanceKm: haversineKm(origin, station), candidatePrice: station.prices?.[fuel] }))
    .filter((station) => Number.isFinite(station.candidatePrice) && station.candidateDistanceKm <= radiusKm);
  const cheapest = [...eligible].sort((a, b) => a.candidatePrice - b.candidatePrice).slice(0, 14);
  const closest = [...eligible].sort((a, b) => a.candidateDistanceKm - b.candidateDistanceKm).slice(0, 8);
  const balanced = [...eligible]
    .sort((a, b) => (a.candidatePrice + a.candidateDistanceKm * 0.002) - (b.candidatePrice + b.candidateDistanceKm * 0.002))
    .slice(0, 10);
  return uniqueCandidates([balanced, cheapest, closest]);
}

function pointToSegmentKm(station, start, end) {
  const latitudeScale = 110.574;
  const longitudeScale = 111.32 * Math.cos((station.lat * Math.PI) / 180);
  const ax = (start[0] - station.lng) * longitudeScale;
  const ay = (start[1] - station.lat) * latitudeScale;
  const bx = (end[0] - station.lng) * longitudeScale;
  const by = (end[1] - station.lat) * latitudeScale;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSquared)) : 0;
  return Math.hypot(ax + t * dx, ay + t * dy);
}

function distanceToRouteKm(station, geometry) {
  let best = Infinity;
  for (let index = 1; index < geometry.length; index += 1) {
    best = Math.min(best, pointToSegmentKm(station, geometry[index - 1], geometry[index]));
  }
  return best;
}

export function selectRouteCandidates(stations, fuel, geometry, maxDetourKm, origin = null, remainingRangeKm = null) {
  if (!Array.isArray(geometry) || geometry.length < 2) return [];
  const step = Math.max(1, Math.ceil(geometry.length / 140));
  const simplified = geometry.filter((_, index) => index % step === 0);
  if (simplified.at(-1) !== geometry.at(-1)) simplified.push(geometry.at(-1));
  const corridorKm = Math.max(5, Math.min(30, maxDetourKm));
  const eligible = stations
    .map((station) => ({
      ...station,
      candidateDistanceKm: distanceToRouteKm(station, simplified),
      candidateReachKm: origin ? haversineKm(origin, station) : null,
      candidatePrice: station.prices?.[fuel],
    }))
    .filter((station) =>
      Number.isFinite(station.candidatePrice)
      && station.candidateDistanceKm <= corridorKm
      && (!Number.isFinite(remainingRangeKm)
        || remainingRangeKm <= 0
        || station.candidateReachKm <= remainingRangeKm),
    );
  const cheapest = [...eligible].sort((a, b) => a.candidatePrice - b.candidatePrice).slice(0, 17);
  const closest = [...eligible].sort((a, b) => a.candidateDistanceKm - b.candidateDistanceKm).slice(0, 10);
  return uniqueCandidates([cheapest, closest]);
}
